'use client';

import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Divider, Box, Typography, Button, CircularProgress } from '@mui/material';
import { Formik } from 'formik';
import { object, mixed, string } from 'yup';

import {
  getVehicleById,
  createVehicle,
  updateVehicle,
  uploadVehicleBarcodeImage,
  deleteVehicleImage,
  deleteVehicleBarcodeImage,
  uploadVehicleImage
} from '../../../stores/masterVehicleStore';
import { getMasterFuelPriceById } from '../../../stores/masterFuelPriceStore';

import InformationForm from './form/InformationForm';
import DocumentForm from './form/DocumentForm';

import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';

import dayjs from 'dayjs';
import {
  deleteVehicleLicenseDetailImage,
  getVehicleLicenseDetailImage,
  uploadVehicleLicenseDetailFile
} from '../../../stores/vehicleLicenseDetailStore';
import { enqueueRetry } from '../../../helpers/retryQueue';
import StorageIcon from '@mui/icons-material/Storage';
import { useLanguage } from '../../../utils/i18n';
import { formatUpperCase } from '../../../utils/formatTitleCase';

const baseInitial = {
  company: null,
  company_name: '',
  vehicle_type: null,
  vehicle_type_name: '',
  assembly_type: '',
  body_type: '',
  vendor: null,
  license_plate: '',
  merk: '',
  model: '',
  year: '',
  status: false,
  is_maintenance: false,
  _image_base64: '',

  qr_pertamina_value: '',
  vehicle_license: [],
  vehicle_license_removed: [],
  fuel_capacity: null,

  fuel_price: null,
  fuel_price_desc: '',
  fuel_price_val: null,
  fuel_price_valid_at: '',
  fuel_type: null,
  fuel_type_category: '',
  fuel_type_code: '',
  fuel_type_desc: '',
  fuel_type_name: '',
  fuel_type_uom: '',

  name: '',
  id: null,
  vendor_name: '',
  chasis_no: '',
  max_speed: '',
  min_speed: '',
  fuel_ratio_km: null,
  fuel_ratio_litre: null
};

const toNumberSafe = (v, fallback = 0) => {
  if (v == null || v === '') return fallback;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isNaN(n) ? fallback : n;
};

const toDateInput = (val) => {
  if (val == null || val === '') return '';
  const s = String(val).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const m2 = s.match(/^(\d{4})[-\/](\d{2})[-\/](\d{2})/);
  if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}`;
  const parsed = dayjs(s);
  if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
  return '';
};

const normalizeVehiclePayload = (values = {}, { forUpdate = false } = {}) => {
  const payload = {
    body_type: values.body_type ?? '',
    company: values.company != null ? Number(values.company) : null,
    helper_id: values.helper_id ?? '',

    is_disposed: !!values.is_disposed,
    is_maintenance: !!values.is_maintenance,
    is_vendor: !!values.is_vendor,
    is_written_off: !!values.is_written_off,

    last_date_service: toDateInput(values.last_date_service ?? null),
    last_date_service2: toDateInput(values.last_date_service2 ?? null),
    last_date_tire: toDateInput(values.last_date_tire ?? null),

    license_plate: formatUpperCase(values.license_plate),

    model: values.model ?? '',
    name: values.name ?? values.name ?? '',
    route_color: '',
    merk: values.merk,

    fuel_capacity: toNumberSafe(values.fuel_capacity, null),
    fuel_type: values.fuel_type != null ? Number(values.fuel_type) : null,
    fuel_price: values.fuel_price != null ? Number(values.fuel_price) : null,
    fuel_price_desc: values.fuel_price_desc || null,
    fuel_price_val: toNumberSafe(values.fuel_price_val, null),
    fuel_price_valid_at: values.fuel_price_valid_at || null,
    fuel_type_category: values.fuel_type_category || null,
    fuel_type_code: values.fuel_type_code || null,
    fuel_type_desc: values.fuel_type_desc || null,
    fuel_type_name: values.fuel_type_name || null,
    fuel_type_uom: values.fuel_type_uom || null,

    qr_pertamina_value: values.qr_pertamina_value || values.license_plate || '',
    status: values.status === undefined ? true : !!values.status,
    stnk_exp_date: values.stnk_exp_date ?? null,
    stnk_number: values.stnk_number ?? '',
    transit_point: values.transit_point != null ? Number(values.transit_point) : null,
    vehicle_type: values.vehicle_type != null ? Number(values.vehicle_type) : null,
    vendor: values.vendor != null && values.vendor !== '' ? Number(values.vendor) : null,
    year: values.year ?? '',
    chasis_no: values.chasis_no ?? '',
    max_speed: toNumberSafe(values.max_speed, 0),
    min_speed: toNumberSafe(values.min_speed, 0),
    fuel_ratio_km: toNumberSafe(values.fuel_ratio_km, null),
    fuel_ratio_litre: toNumberSafe(values.fuel_ratio_litre, null)
  };

  const vlArray = (Array.isArray(values.vehicle_license) ? values.vehicle_license : []).map((vl) => {
    return {
      id: vl.id ?? undefined,
      vehicle: values?.id ?? values?.vehicle ?? null,
      license_name: vl.license_name ?? vl.license_type_label ?? null,
      vehicle_license: vl.vehicle_license ?? vl.license_type ?? null,
      file_license: vl.file_license ?? null,
      license_no: vl.license_no ?? null,
      license_exp_date: vl.is_lifetime ? null : vl.license_exp_date ? toDateInput(vl.license_exp_date) : null,
      front_photo: vl.front_photo ?? vl.front_photo ?? null,
      back_photo: vl.back_photo ?? vl.back_photo ?? null,
      data_front_photo: vl.data_front_photo ?? {},
      data_back_photo: vl.data_back_photo ?? {},
      photo: vl.photo ?? null,
      url_license: vl.url_license ?? null,
      is_lifetime: Boolean(vl.is_lifetime)
    };
  });

  if (vlArray.length) payload.vehicle_license = vlArray;

  if (Array.isArray(values.vehicle_license_removed) && values.vehicle_license_removed.length) {
    payload.vehicle_license_removed = values.vehicle_license_removed;
  }

  if (values._image_base64) {
    payload.photo = values._image_base64;
  } else if (values.photo) {
    payload.photo = values.photo;
  }

  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k];
  });

  if (forUpdate) {
    if (values.id != null) payload.id = values.id;
  } else {
    delete payload.id;
  }

  return payload;
};

const MasterVehicleAddEdit = forwardRef(
  ({ open, id, onClose, onSuccess, onError, onSubmitStart = () => {}, onSubmitEnd = () => {} }, ref) => {
    const { t } = useLanguage();
    const [initialValues, setInitialValues] = useState(baseInitial);

    // filterCompanyId sekarang sepenuhnya dikontrol dari dalam Formik values,
    // jadi state di level ini bisa dihapus jika tidak digunakan untuk fetching di level ini.
    const notify = useNotifier();
    const informationRef = useRef();
    const documentRef = useRef();
    const formikRef = useRef();

    const [formKey, setFormKey] = useState(0);

    useImperativeHandle(ref, () => ({
      submitForm: async () => {
        await formikRef.current?.submitForm();
        return formikRef.current?.errors;
      }
    }));

    const isCompleteFillLicense = (license) => {
      if (!license?.join?.vehicle_license_license_name && !license?.license_name && !license?.license_type_label) return false;
      if (!license?.is_lifetime && !license?.license_exp_date) return false;
      if (!license?.license_no) return false;

      const hasFront = !!license.front_photo;
      const hasBack = !!license.back_photo;
      const hasFile = !!license.url_license;

      if (hasFront || hasBack || hasFile) return true;

      return false;
    };

    const getFileExtensionFromUrl = (url) => {
      try {
        const cleanUrl = url.split('?')[0];
        return cleanUrl.split('.').pop().toLowerCase();
      } catch {
        return null;
      }
    };

    const handleGetFileLicense = async (id) => {
      let document = '',
        frontImage = '',
        backImage = '';
      try {
        document = await getVehicleLicenseDetailImage(id, 'document');
        if (document) {
          const extFile = getFileExtensionFromUrl(document);
          if (extFile === 'pdf') return { document: document, front: null, back: null };
        }
      } catch (err) {
        console.warn('Failed to load document image for license', id, err);
      }

      try {
        frontImage = await getVehicleLicenseDetailImage(id, 'front');
      } catch (err) {
        console.warn('Failed to load front image for license', id, err);
      }

      try {
        backImage = await getVehicleLicenseDetailImage(id, 'back');
      } catch (err) {
        console.warn('Failed to load back image for license', id, err);
      }

      return { document: document || '', front: frontImage || '', back: backImage || '' };
    };

    const handleProcessLicense = async (license) => {
      const result = [];
      if (license && license.length > 0) {
        for (let index = 0; index < license.length; index++) {
          let data = license[index];
          const fileData = await handleGetFileLicense(data.id);
          data.front_photo = fileData.front;
          data.back_photo = fileData.back;
          data.url_license = fileData.document;
          data.is_completed = isCompleteFillLicense(data);
          result.push(data);
        }
      }
      return result;
    };

    useEffect(() => {
      let mounted = true;

      if (!open) {
        const timer = setTimeout(() => {
          setInitialValues(baseInitial);
          formikRef.current?.resetForm?.({ values: baseInitial });
        }, 500);
        return () => clearTimeout(timer);
      } else {
        // Increment formKey when opened, triggering a fresh unmount/remount
        // of Formik and all children.
        setFormKey((prev) => prev + 1);
      }

      const fetch = async () => {
        try {
          if (id) {
            const data = await getVehicleById(id);
            if (!mounted) return;
            if (data) {
              const vehicleLicense = await handleProcessLicense(data.vehicle_license);
              const extractId = (val) => {
                if (typeof val === 'object' && val !== null) return val.id ?? val.value;
                return val;
              };

              let initialFuelTypeId =
                extractId(
                  data.fuel_type ??
                    data.fuel_type_id ??
                    data?.fuel_type_join?.id ??
                    data?.join?.fuel_type_id ??
                    data?.join?.fuel_type ??
                    data?.master_fuel_price_join?.fuel_type_id ??
                    data?.master_fuel_price_join?.fuel_type
                ) ?? '';

              const fpId = data.fuel_price || data.fuel_price_id || '-';
              if ((!initialFuelTypeId || String(initialFuelTypeId) === '0') && fpId && String(fpId) !== '0') {
                try {
                  const fp = await getMasterFuelPriceById(fpId);
                  const fpData = fp?.data ?? fp;
                  if (fpData) {
                    initialFuelTypeId =
                      extractId(fpData.fuel_type ?? fpData.fuel_type_id ?? fpData?.fuel_type_join?.id ?? fpData?.join?.fuel_type_id) ?? '';
                  }
                } catch (e) {
                  console.error('Failed to fetch master fuel price for initial fuel_type:', e);
                }
              }

              setInitialValues((prev) => ({
                ...prev,
                company: extractId(data.company ?? data.company_id) ?? null,
                vehicle_type: extractId(data.vehicle_type ?? data.vehicle_type_id) ?? null,
                vendor: extractId(data.vendor ?? data.vendor_id) ?? null,
                transit_point: extractId(data.transit_point ?? data.transit_point_id ?? data.depot_id) ?? null,
                body_type: data.body_type ?? '',
                company_name: data.company_name ?? data.company_label ?? prev.company_name,
                vehicle_type_name: data.vehicle_type_name ?? prev.vehicle_type_name,
                vendor_name: data.vendor_name ?? data.join?.vendor_name ?? prev.vendor_name,
                transit_point_name: data.transit_point_name ?? data.depot_name ?? prev.transit_point_name,
                fuel_capacity: data.fuel_capacity ?? 0,
                fuel_type: initialFuelTypeId && String(initialFuelTypeId) !== '0' ? initialFuelTypeId : '',
                fuel_price: Number(data.fuel_price) > 0 ? Number(data.fuel_price) : null,
                fuel_price_desc: data.fuel_price_desc ?? '',
                fuel_price_val: data.fuel_price_val ?? 0,
                fuel_price_valid_at: data.fuel_price_valid_at ?? '',
                fuel_type_category: data.fuel_type_category ?? '',
                fuel_type_code: data.fuel_type_code ?? '',
                fuel_type_desc: data.fuel_type_desc ?? '',
                fuel_type_name: data.fuel_type_name ?? '',
                fuel_type_uom: data.fuel_type_uom ?? '',
                license_plate: data.license_plate ?? '',
                merk: data.merk ?? data.manufactur ?? '',
                model: data.model ?? '',
                year: data.year ?? '',
                status: data.status ?? true,
                is_maintenance: Boolean(data.is_maintenance),
                _image_base64: data.photo ?? '',
                qr_pertamina_value: data.qr_pertamina_value ?? '',
                vehicle_license: Array.isArray(data.vehicle_license) ? vehicleLicense : [],
                id: data.id ?? prev.id,
                name: data.name ?? prev.name,
                chasis_no: data.chasis_no ?? '',
                max_speed: data.max_speed ?? '',
                min_speed: data.min_speed ?? '',
                fuel_ratio_km: data.fuel_ratio_km ?? null,
                fuel_ratio_litre: data.fuel_ratio_litre ?? null
              }));
            }
          } else {
            // Ketika tidak ada ID (Mode Add), coba ambil company default dari LocalStorage
            let nextCompanyId = null;
            try {
              const storedCompanyId = localStorage.getItem('companyId');
              if (storedCompanyId) {
                nextCompanyId = storedCompanyId;
              }
            } catch (err) {
              console.warn('Init company from localStorage failed', err);
            }

            setInitialValues((prev) => ({
              ...baseInitial,
              company: nextCompanyId
            }));
          }
        } catch (err) {
          console.error(err);
          onError && onError('Gagal memuat data');
        }
      };

      fetch();

      return () => {
        mounted = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, open, onError]);

    const validationSchema = object().shape({
      company: mixed().required('Company is mandatory'),
      license_plate: string().required('Plate is mandatory'),
      name: string().required('Body Name is mandatory'),
      vehicle_type: mixed().required('Vehicle Type is mandatory')
    });

    const handleSubmit = async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSubmitStart();

      try {
        const payload = normalizeVehiclePayload(values, { forUpdate: !!id });
        let vehicleId;
        const vehicleImages = values.vehicle_photo || [];
        const licenseItems = values.vehicle_license || [];
        const qrFile = values.qr_pertamina_file_obj;

        //==============SUBMIT UTAMA==============
        if (id) {
          await updateVehicle(id, payload);
          vehicleId = id;
        } else {
          const response = await createVehicle(payload);
          vehicleId = response?.id || response?.data?.id || response?.result?.id;
        }

        if (!vehicleId) throw new Error('Failed to retrieve Vehicle ID (Backend mungkin tidak mengembalikan ID)');

        //==============HELPER AMBIL NAMA FILE==============
        const getFileNameFromUrl = (url) => {
          if (!url || url.startsWith('blob:')) return '';
          try {
            const cleanUrl = url.split('?')[0];
            const fileName = cleanUrl.split('/').pop();
            return fileName;
          } catch {
            return '';
          }
        };

        //==============DELETE PHOTO VEHICLE==============
        const photosToDelete = values._deleted_vehicle_photos || [];
        if (photosToDelete.length > 0) {
          for (const photoUrl of photosToDelete) {
            try {
              const fileName = getFileNameFromUrl(photoUrl);
              if (fileName) {
                await deleteVehicleImage(vehicleId, fileName);
              }
            } catch (err) {
              console.error(`Failed to delete image ${photoUrl}:`, err);
            }
          }
        }

        //==============DELETE BARCODE MY PERTAMINA==============
        if (values._delete_qr_code && values._deleted_qr_url) {
          try {
            const qrFileName = getFileNameFromUrl(values._deleted_qr_url);
            if (qrFileName) {
              await deleteVehicleBarcodeImage(vehicleId, qrFileName);
            }
          } catch (err) {
            console.error('Failed to delete barcode:', err);
          }
        }

        //==============FUNGSI UPLOAD FOTO VEHICLE==============
        const latestVehicle = await getVehicleById(vehicleId);
        const serverLicenses = latestVehicle?.vehicle_license || [];
        for (let f of vehicleImages) {
          if (f instanceof File) {
            await uploadVehicleImage(vehicleId, f).catch((err) => {
              console.error('Vehicle image upload failed, queuing for retry:', err);
              enqueueRetry({ type: 'vehicle-image', action: () => uploadVehicleImage(vehicleId, f) });
            });
          }
        }

        //==============FUNGSI UPLOAD QRCODE==============
        if (qrFile instanceof File) {
          await uploadVehicleBarcodeImage(vehicleId, qrFile).catch((err) => {
            console.error('QR upload failed', err);
            enqueueRetry({
              type: 'vehicle-qr',
              action: () => uploadVehicleBarcodeImage(vehicleId, qrFile)
            });
          });
        }

        if (id) {
          const removedLicenseIds = values.vehicle_license_removed || [];
          const imagesToDelete = values._images_to_delete ?? [];
          const uniqueImagesToDelete = imagesToDelete.filter(
            (rf, index, self) =>
              rf?.id &&
              rf?.type &&
              rf?.fileName &&
              !removedLicenseIds.includes(rf.id) &&
              index === self.findIndex((t) => t.id === rf.id && t.type === rf.type && t.fileName === rf.fileName)
          );
          for (let rf of uniqueImagesToDelete) {
            await deleteVehicleLicenseDetailImage(rf.id, rf.type, rf.fileName);
          }
        }

        //==============FUNGSI UPLOUAD DOCUMENT LICENSE==============
        for (let item of licenseItems) {
          const matched = serverLicenses.find((vl) => String(vl.license_no) === String(item.license_no));
          if (matched && matched.id) {
            if (item.front_photo_file instanceof File) {
              await uploadVehicleLicenseDetailFile(matched.id, item.front_photo_file, 'front').catch(() => {
                enqueueRetry({ type: 'v-lic', action: () => uploadVehicleLicenseDetailFile(matched.id, item.front_photo_file, 'front') });
              });
            }
            if (item.back_photo_file instanceof File) {
              await uploadVehicleLicenseDetailFile(matched.id, item.back_photo_file, 'back').catch(() => {
                enqueueRetry({ type: 'v-lic', action: () => uploadVehicleLicenseDetailFile(matched.id, item.back_photo_file, 'back') });
              });
            }
            if (item.document_file instanceof File) {
              await uploadVehicleLicenseDetailFile(matched.id, item.document_file, 'document').catch(() => {
                enqueueRetry({ type: 'v-lic', action: () => uploadVehicleLicenseDetailFile(matched.id, item.document_file, 'document') });
              });
            }
          } else {
            console.warn(`No match found for license number: ${item.license_no}. File upload skipped.`);
          }
        }

        notify({ message: id ? 'Updated successfully' : 'Created successfully', severity: 'success' });
        onSuccess?.(id ? 'Updated successfully' : 'Created successfully');
        onClose?.();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
        onSubmitEnd();
      }
    };

    return (
      <Formik
        key={formKey}
        innerRef={formikRef}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          return (
            <FormDialog
              open={open}
              onClose={onClose}
              title={id ? t('masterVehicle.edit', 'Edit Vehicle') : t('masterVehicle.add', 'Add Vehicle')}
              fullScreenMode={true}
              fullHeight={false}
              actions={
                <>
                  <Button
                    variant="contained"
                    onClick={() => {
                      formik.validateForm().then((errs) => {
                        if (Object.keys(errs).length > 0) {
                          notify({ message: t('validation.requiredFields', 'Please fill all required fields'), severity: 'error' });
                        }
                      });
                    }}
                    type="submit"
                    form="master-vehicle-form"
                    disabled={formik.isSubmitting}
                    sx={{ backgroundColor: '#0072DA' }}
                    startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                  >
                    {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
                  </Button>
                  <Button onClick={onClose} disableRipple disableElevation variant="outlined">
                    {t('buttonTitle.cancel', 'Cancel')}
                  </Button>
                </>
              }
            >
              <Box>
                <form id="master-vehicle-form" onSubmit={formik.handleSubmit}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'start'
                        }}
                      >
                        <StorageIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                      </Box>
                      <Typography variant="h4">{t('masterVehicle.master', 'Master Vehicle')}</Typography>
                    </Box>
                  </Box>

                  {/* Panggil komponen anak tanpa melemparkan option props karena sudah di-fetch di dalam masing-masing komponen */}
                  <InformationForm ref={informationRef} formik={formik} />

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'start'
                        }}
                      >
                        <StorageIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                      </Box>
                      <Typography variant="h4">{t('masterVehicle.vehicleLicense', 'Vehicle License')}</Typography>
                    </Box>
                  </Box>

                  <DocumentForm ref={documentRef} formik={formik} />
                </form>
              </Box>
            </FormDialog>
          );
        }}
      </Formik>
    );
  }
);

export default MasterVehicleAddEdit;
